import { useState } from "react";
import { Minus, Plus } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import NavigationToast from "../../components/Toast/NavigationToast";
import { updateCartItemQuantity } from "../../redux/slices/cartSlice";
import ShipSellModal from "../../components/SellItemsConfirm/ShipSellModal";
import ProductRightSlider from "../../components/ProductRightSlider/ProductRightSlider";
import { ReactComponent as Eye } from "../../assets/icons/Eye.svg";

function CartItem({
  item,
  isSelected,
  isExpanded,
  onWithdraw,
  onToggleSelect,
}) {
  const dispatch = useDispatch();
  const quantity = item.quantity ?? 1;
  const [overlayData, setoverlayData] = useState();
  const [isHovered, setIsHovered] = useState(false);
  const [navigationToast, setNavigationToast] = useState({
    error: "",
    success: "",
    redirectTo: "",
  });
  const [showOverlay, setShowOverlay] = useState(false);
  const showQuantityControls = isHovered || isSelected || isExpanded;
  const showItemActions = (isHovered || isExpanded) && !isSelected;
  const showHoverActions = isHovered || isSelected || isExpanded;
  const isDarkMode = useSelector((state) => state?.theme?.darkMode);
  const [modalState, setModalShow] = useState({ show: false, type: "" });

  const handleQuantityChange = (change) => {
    dispatch(
      updateCartItemQuantity({
        id: item.id,
        change,
      }),
    );
  };

  // Check if on mobile
  const isMobile = window.innerWidth <= 768;

  return (
    <>
      <NavigationToast message={navigationToast} />

      <div
        className={`cart-item-row ${
          isDarkMode ? "cart-item-row-dark dark" : "cart-item-row-light light"
        } ${isSelected ? "cart-item-row-selected" : ""} ${showHoverActions ? "cart-item-row-hovered" : ""}`}
        onClick={(e) => {
          if (e.target.type !== "checkbox") {
            onToggleSelect(item);
          }
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Checkbox */}
        <div className="cart-item-checkbox">
          <input
            type="checkbox"
            checked={isSelected}
            className="custom-checkbox-brz"
            onChange={(e) => {
              e.stopPropagation();
              onToggleSelect(item);
            }}
          />
        </div>

        {/* Product Image */}
        <div className="cart-item-image-wrapper">
          <img
            src={item?.image}
            alt={
              item?.name?.length > 4
                ? `${item.name.substring(0, 4)}...`
                : item?.name
            }
            className="cart-item-image"
          />
        </div>

        {!isMobile ? (
          <>
            <div className="cart-item-name">
              <span
                className={
                  isDarkMode ? "cart-item-name-dark" : "cart-item-name-light"
                }
              >
                {item?.name}
              </span>
            </div>

            <div className="cart-item-price">
              <span className="cart-item-price-text">${item?.price}</span>
            </div>
          </>
        ) : (
          <>
            <div className="cart-item-name">
              <span
                className={
                  isDarkMode ? "cart-item-name-dark" : "cart-item-name-light"
                }
              >
                {item?.name}
              </span>
              <span className="cart-item-price-text">${item?.price}</span>
            </div>
          </>
        )}

        {/* Desktop Hover Actions - Hide on mobile */}
        {!isMobile && (
          <>
            {/* Quantity Controls */}
            {showQuantityControls && (
              <div
                className={`cart-item-quantity-control ${
                  isDarkMode
                    ? "cart-item-quantity-control-dark"
                    : "cart-item-quantity-control-light"
                }`}
              >
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleQuantityChange(-1);
                  }}
                  disabled={quantity <= 1}
                  className={`quantity-btn ${
                    quantity <= 1 ? "cursor-not-allowed" : ""
                  } ${isDarkMode ? "quantity-btn-dark" : "quantity-btn-light"}`}
                >
                  <Minus width="16" height="16" />
                </button>

                <span
                  className={`cart-quantity-display ${
                    isDarkMode
                      ? "quantity-display-dark"
                      : "quantity-display-light"
                  }`}
                >
                  {quantity}
                </span>

                <button
                  disabled={quantity >= item.stock}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleQuantityChange(1);
                  }}
                  className={`quantity-btn ${
                    quantity >= item.stock ? "cursor-not-allowed" : ""
                  } ${isDarkMode ? "quantity-btn-dark" : "quantity-btn-light"}`}
                >
                  <Plus width="16" height="16" />
                </button>
              </div>
            )}

            {showItemActions && (
              <>
                <button
                  disabled={item?.digital}
                  className={`action-btn ${
                    item?.digital
                      ? "cursor-not-allowed opacity-50 action-btn-ship-disabled"
                      : "action-btn-ship"
                  }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setModalShow({ show: true, type: "ship" });
                  }}
                >
                  Ship
                </button>

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onWithdraw();
                  }}
                  className="action-btn action-btn-withdraw"
                >
                  Withdraw
                </button>

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setModalShow({ show: true, type: "sell" });
                  }}
                  className="action-btn action-btn-sell"
                >
                  Sell
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setoverlayData(item);
                    setShowOverlay((prevState) => !prevState);
                  }}
                  className={`view-btn ${
                    isDarkMode ? "view-btn-dark" : "view-btn-light"
                  }`}
                ></button>
              </>
            )}
            <div
              className="cart-item-view"
              onClick={(e) => {
                e.stopPropagation();
                setoverlayData(item);
                setShowOverlay((prevState) => !prevState);
              }}
              style={{ marginLeft: "auto", cursor: "pointer" }}
            >
              {" "}
              <Eye
                className="view-icon"
                color={isDarkMode ? "#000000" : "#A3A3A3"}
              />
            </div>
          </>
        )}

        {/* View Icon - Always Visible (acts as expand button on mobile) */}
        <div className="cart-item-view">
          {isMobile && isSelected && (
            <div
              className={`cart-item-quantity-control mobile-quantity ${
                isDarkMode
                  ? "cart-item-quantity-control-dark"
                  : "cart-item-quantity-control-light"
              }`}
            >
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleQuantityChange(-1);
                }}
                className={`quantity-btn ${
                  isDarkMode ? "quantity-btn-dark" : "quantity-btn-light"
                }`}
              >
                <Minus width="16" height="16" />
              </button>
              <span
                className={`quantity-display ${
                  isDarkMode
                    ? "quantity-display-dark"
                    : "quantity-display-light"
                }`}
              >
                {quantity}
              </span>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleQuantityChange(1);
                }}
                className={`quantity-btn ${
                  isDarkMode ? "quantity-btn-dark" : "quantity-btn-light"
                }`}
              >
                <Plus width="16" height="16" />
              </button>
            </div>
          )}
          <button
            onClick={(e) => {
              e.stopPropagation();
              setoverlayData(item);
              setShowOverlay((prevState) => !prevState);
            }}
            className={`view-btn d-lg-none ${
              isDarkMode ? "view-btn-dark" : "view-btn-light"
            }`}
          >
            <Eye
              className="view-icon"
              color={isDarkMode ? "white" : "#A3A3A3"}
            />
          </button>
        </div>

        {/* Mobile Expandable Actions - Show below the item row */}
        {isMobile && isExpanded && (
          <div className="mobile-actions-container">
            {/* Action Buttons */}
            <div className="mobile-action-buttons">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setModalShow({ show: true, type: "ship" });
                }}
                className="action-btn action-btn-ship mobile-btn"
              >
                Ship
              </button>

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onWithdraw();
                }}
                className="action-btn action-btn-withdraw mobile-btn"
              >
                Withdraw
              </button>

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setModalShow({ show: true, type: "sell" });
                }}
                className="action-btn action-btn-sell mobile-btn"
              >
                Sell
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Ship & Sell Modal */}
      <ShipSellModal
        item={item}
        show={modalState.show}
        type={modalState.type}
        setModalShow={setModalShow}
        setNavigationToast={setNavigationToast}
      />
      <ProductRightSlider
        isCart={true}
        showOverlay={showOverlay}
        overlayData={overlayData}
        setShowOverlay={setShowOverlay}
      />
    </>
  );
}

export default CartItem;
